/**
 * Users Repository Implementation (Infrastructure)
 *
 * Implements the domain repository interface.
 * Similar to Flutter's repository implementation pattern.
 *
 * This layer is responsible for:
 * - Coordinating datasources
 * - Error handling and transformation
 * - Caching strategies (if needed)
 * - Data mapping between layers
 */

import { AppException, ErrorCode, NotFoundException } from '@/core/exceptions';

import type { UsersRepository, UpdateUserRequest } from '../../domain';
import type { UserDetail } from '../../domain/types';
import type { UserSearchRequest } from '../../domain/types/requests';
import type { UserSearchResponse, UserSearchOptionsResponse } from '../../domain/types/responses';
import type { UsersDatasource } from '../datasources';
import { usersDatasource } from '../datasources';

// ============================================================================
// Repository Implementation
// ============================================================================

export class UsersRepositoryImpl implements UsersRepository {
  constructor(private readonly datasource: UsersDatasource = usersDatasource) {}

  async searchUsers(request: UserSearchRequest): Promise<UserSearchResponse> {
    try {
      return await this.datasource.searchUsers(request);
    } catch (error) {
      if (error instanceof NotFoundException) {
        return { users: [], total: 0, page: request.page ?? 1, pages: 1 };
      }
      throw this.handleError(error, 'Failed to search users');
    }
  }

  async getSearchOptions(): Promise<UserSearchOptionsResponse> {
    try {
      return await this.datasource.getSearchOptions();
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch search options');
    }
  }

  async updateUser(request: UpdateUserRequest): Promise<UserDetail> {
    try {
      return await this.datasource.updateUser(request);
    } catch (error) {
      throw this.handleError(error, 'Failed to update user');
    }
  }

  /**
   * Transform errors to domain exceptions
   */
  private handleError(error: unknown, fallbackMessage: string): AppException {
    if (error instanceof AppException) {
      return error;
    }

    if (error instanceof Error) {
      return new AppException({
        code: ErrorCode.UNKNOWN_ERROR,
        message: error.message,
        userMessage: error.message || fallbackMessage,
      });
    }

    return new AppException({
      code: ErrorCode.UNKNOWN_ERROR,
      message: fallbackMessage,
      userMessage: fallbackMessage,
    });
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const usersRepository: UsersRepository = new UsersRepositoryImpl();
